import React, { useState, useEffect } from 'react';
import './LinkPreview.css';

interface PreviewData {
    title?: string;
    description?: string;
    image?: string;
    domain: string;
}

interface LinkPreviewProps {
    url: string;
}

const CACHE_KEY = 'link-preview-cache';

function getCache(): Record<string, PreviewData> {
    try {
        return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    } catch {
        return {};
    }
}

function setCache(url: string, data: PreviewData) {
    try {
        const cache = getCache();
        cache[url] = data;
        localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch { /* ignore quota errors */ }
}

const LinkPreview: React.FC<LinkPreviewProps> = ({ url }) => {
    const domain = new URL(url).hostname.replace('www.', '');
    const cached = getCache()[url];
    const [data, setData] = useState<PreviewData | null>(cached || null);
    const [loading, setLoading] = useState(!cached);

    useEffect(() => {
        if (cached) return;

        const fetchPreview = async () => {
            try {
                const response = await fetch(
                    `https://api.microlink.io/?url=${encodeURIComponent(url)}`
                );
                const result = await response.json();
                if (result.status === 'success' && result.data) {
                    const preview: PreviewData = {
                        title: result.data.title,
                        description: result.data.description,
                        image: result.data.image?.url,
                        domain
                    };
                    setData(preview);
                    setCache(url, preview);
                } else {
                    const fallback = { domain };
                    setData(fallback);
                    setCache(url, fallback);
                }
            } catch {
                const fallback = { domain };
                setData(fallback);
                setCache(url, fallback);
            } finally {
                setLoading(false);
            }
        };
        fetchPreview();
    }, [url, domain, cached]);

    if (loading) {
        return (
            <div className="link-preview link-preview-loading">
                <div className="link-preview-image-skeleton" />
                <div className="link-preview-content">
                    <div className="link-preview-text-skeleton" />
                    <div className="link-preview-text-skeleton short" />
                </div>
            </div>
        );
    }

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="link-preview"
        >
            {data?.image && (
                <div className="link-preview-image">
                    <img src={data.image} alt={data.title || ''} />
                </div>
            )}
            <div className="link-preview-content">
                <span className="link-preview-domain">{data?.domain || domain}</span>
                {data?.title && (
                    <h3 className="link-preview-title">{data.title}</h3>
                )}
                {data?.description && (
                    <p className="link-preview-description">{data.description}</p>
                )}
            </div>
        </a>
    );
};

export default LinkPreview;
